import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  IconButton,
  Box,
  Stack,
  CircularProgress,
  Alert,
  Typography,
  Divider,
  Paper,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import InfoIcon from "@mui/icons-material/Info";
import { editRenew } from "../../../Services/RenewTanentPlaneApi";

// 🔧 Helper لتحويل التاريخ إلى YYYY-MM-DD
const toDateInputFormat = (dateString) => {
  if (!dateString) return "";
  try {
    return new Date(dateString).toISOString().split("T")[0];
  } catch {
    return "";
  }
};

const EditRenewDialog = ({ open, onClose, renew = null, renewId = null, defaults = {}, onSuccess }) => {
  const id = renew?.id ?? renewId;

  const [form, setForm] = useState({
    tenant_id: "",
    cost: "",
    renew_date: "",
    start_date: "",
    end_date: "",
    employee_name: "",
    notes: "",
    file: null,
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // تحميل البيانات داخل الفورم عند فتح الديالوج
  useEffect(() => {
    if (!open) return;

    setForm({
      tenant_id: renew?.tenant_id ?? renew?.tenant?.id ?? "",
      cost: renew?.cost ?? "",
      renew_date: toDateInputFormat(renew?.renew_date),
      start_date: toDateInputFormat(renew?.start_date ?? renew?.tenant?.start_date),
      end_date: toDateInputFormat(renew?.end_date ?? renew?.tenant?.end_date),
      employee_name: renew?.employee_name ?? "",
      notes: renew?.notes ?? "",
      file: null,
    });

    setError("");
  }, [open, renew]);

  const handleChange = (key) => (e) => {
    const value = key === "file" ? e.target.files?.[0] ?? null : e.target.value;
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const validate = () => {
    if (!id) return "معرّف التجديد غير موجود.";
    if (!form.cost) return "التكلفة مطلوبة.";
    if (!form.renew_date) return "تاريخ التجديد مطلوب.";
    return null;
  };

  const handleSubmit = async () => {
    setError("");

    const validation = validate();
    if (validation) {
      setError(validation);
      return;
    }

    setLoading(true);
    try {
      const payload = {
        tenant_id: form.tenant_id,
        cost: form.cost,
        renew_date: form.renew_date,
        start_date: form.start_date,
        end_date: form.end_date,
        employee_name: form.employee_name,
        notes: form.notes,
      };

      if (form.file) payload.file = form.file;

      const res = await editRenew(id, payload);
      const updatedRecord = res?.data?.data ?? res?.data ?? res;

      onSuccess && onSuccess(updatedRecord, payload, id);
      onClose && onClose();
    } catch (err) {
      console.error("editRenew error:", err);
      setError(err?.response?.data?.message || "فشل تعديل التجديد");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog 
      open={Boolean(open)} 
      onClose={onClose} 
      fullWidth 
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: "0 8px 32px rgba(255, 106, 0, 0.2)",
        }
      }}
    >
      <DialogTitle 
        sx={{ 
          display: "flex", 
          justifyContent: "space-between", 
          alignItems: "center",
          background: "linear-gradient(135deg, #ff9a56 0%, #ff6a00 100%)",
          color: "#fff",
          py: 2.5,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <EditIcon />
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            ✏️ تعديل تجديد #{id ?? ""}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ color: "#fff" }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 3, background: "#fafafa" }}>
        <Stack spacing={2.5}>
          {error && <Alert severity="error">{error}</Alert>}

          {/* عرض معلومات السجل الحالي */}
          {renew && (
            <Paper 
              elevation={2} 
              sx={{ 
                p: 2, 
                mb: 1, 
                borderRadius: 2, 
                background: "linear-gradient(135deg, #fff5e6 0%, #ffe0b2 100%)",
                border: "1px solid #ff9a56"
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                <InfoIcon sx={{ color: "#ff6a00", fontSize: 20 }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#ff6a00" }}>
                  📋 البيانات الحالية
                </Typography>
              </Box>
              <Divider sx={{ mb: 1.5, borderColor: "#ff9a56" }} />
              <Typography variant="body2" sx={{ mb: 0.5, color: "#555" }}>
                💰 التكلفة: <strong>{renew.cost ?? "-"}</strong>
              </Typography>
              <Typography variant="body2" sx={{ mb: 0.5, color: "#555" }}>
                📅 تاريخ التجديد: <strong>{renew.renew_date ?? "-"}</strong>
              </Typography>
              <Typography variant="body2" sx={{ color: "#555" }}>
                👤 الموظف: <strong>{renew.employee_name ?? "-"}</strong>
              </Typography>
            </Paper>
          )}

          <TextField 
            label="معرف المستأجر" 
            value={form.tenant_id} 
            fullWidth 
            disabled 
            sx={{
              "& .MuiOutlinedInput-root": {
                background: "#fff",
              }
            }}
          />

          <TextField
            label="التكلفة"
            value={form.cost}
            onChange={handleChange("cost")}
            fullWidth
            sx={{
              "& .MuiOutlinedInput-root": {
                background: "#fff",
                "&.Mui-focused fieldset": { borderColor: "#ff6a00" }
              }
            }}
          />

          <Stack direction="row" spacing={1.5}>
            <TextField
              label="تاريخ التجديد"
              type="date"
              value={form.renew_date}
              onChange={handleChange("renew_date")}
              InputLabelProps={{ shrink: true }}
              fullWidth
              sx={{
                "& .MuiOutlinedInput-root": {
                  background: "#fff",
                  "&.Mui-focused fieldset": { borderColor: "#ff6a00" }
                }
              }}
            />

            <TextField
              label="بداية الباقة"
              type="date"
              value={form.start_date}
              onChange={handleChange("start_date")}
              InputLabelProps={{ shrink: true }}
              fullWidth
              sx={{
                "& .MuiOutlinedInput-root": {
                  background: "#fff",
                  "&.Mui-focused fieldset": { borderColor: "#ff6a00" }
                }
              }}
            />

            <TextField
              label="نهاية الباقة"
              type="date"
              value={form.end_date}
              onChange={handleChange("end_date")}
              InputLabelProps={{ shrink: true }}
              fullWidth
              sx={{
                "& .MuiOutlinedInput-root": {
                  background: "#fff",
                  "&.Mui-focused fieldset": { borderColor: "#ff6a00" }
                }
              }}
            />
          </Stack>

          <TextField
            label="اسم الموظف"
            value={form.employee_name}
            onChange={handleChange("employee_name")}
            fullWidth
            sx={{
              "& .MuiOutlinedInput-root": {
                background: "#fff",
                "&.Mui-focused fieldset": { borderColor: "#ff6a00" }
              }
            }}
          />

          <TextField
            label="ملاحظات"
            value={form.notes}
            onChange={handleChange("notes")}
            fullWidth
            multiline
            rows={3}
            sx={{
              "& .MuiOutlinedInput-root": {
                background: "#fff",
                "&.Mui-focused fieldset": { borderColor: "#ff6a00" }
              }
            }}
          />

          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Button 
              variant="outlined" 
              component="label" 
              size="medium"
              startIcon={<AttachFileIcon />}
              sx={{ 
                borderColor: "#ff6a00", 
                color: "#ff6a00",
                textTransform: "none",
                fontWeight: 600,
                "&:hover": { 
                  borderColor: "#ff6a00", 
                  background: "rgba(255, 106, 0, 0.05)" 
                }
              }}
            >
              رفع ملف (اختياري)
              <input type="file" hidden onChange={handleChange("file")} />
            </Button>
            {form.file && (
              <Typography variant="body2" sx={{ color: "#555", fontWeight: 600 }}>
                📎 {form.file.name}
              </Typography>
            )}
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: 2.5, background: "#fafafa" }}>
        <Button 
          onClick={onClose} 
          disabled={loading}
          sx={{ 
            textTransform: "none", 
            fontWeight: 600,
            color: "#666"
          }}
        >
          إلغاء
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          disabled={loading}
          startIcon={loading ? null : <SaveIcon />}
          sx={{ 
            background: "linear-gradient(135deg, #ff9a56 0%, #ff6a00 100%)",
            color: "#fff",
            textTransform: "none",
            fontWeight: 700,
            px: 3,
            "&:hover": {
              background: "linear-gradient(135deg, #ff8c43 0%, #e65c00 100%)",
            }
          }}
        >
          {loading ? <CircularProgress size={20} color="inherit" /> : "حفظ التعديل"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditRenewDialog;
